import React, { useState, useEffect } from 'react';
import { X, User, MapPin, Phone, Clock, FileText, Calendar, ExternalLink, ClipboardList, UserCheck, PlayCircle, CheckCircle2, Star, RefreshCw, Package, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import CallButton from '../clicktocall/CallButton';
import JobAssignModal from '../scheduler/JobAssignModal';
import { useTasks } from '../../context/TasksContext';
import { useCompany } from '../../context/CompanyContext';
import FlashPill from '../propertyDetails/FlashPill';
import OverrideChip from '../overrides/OverrideChip';
import { resolveDisplayedStep } from '../../utils/resolveDisplayedStep';
import ConfirmCompleteDialog from '../jobs/ConfirmCompleteDialog';
import PerVisitBillingOverride from '../jobs/PerVisitBillingOverride';
import { findEarliestLiveRecurringTask } from '../../utils/buildForecast';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * JobDetailsPopCard - Read-only inspection surface for scheduled jobs
 * 
 * Opens on click (not drag) to show job/client details without mutating schedule.
 * Actions available: View Profile, View Details, Edit Schedule, Unschedule
 */
const JobDetailsPopCard = ({ 
  task, 
  lead, 
  isOpen, 
  onClose, 
  onUnschedule,
  onEditSchedule,
  position = { top: 0, left: 0 }
}) => {
  const navigate = useNavigate();
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [sendingReview, setSendingReview] = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const { updateTask, completeJob, tasks } = useTasks();
  const { companyId } = useCompany();

  // Escape-key close. Only bound while the dialog is open so we don't leak
  // listeners on remount. Preserves the existing `onClose` contract.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !task) return null;

  // Format time display
  const formatTime = (dateStr) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid';
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Calculate duration
  const getDuration = () => {
    if (!task.scheduledStart || !task.scheduledEnd) return null;
    const start = new Date(task.scheduledStart);
    const end = new Date(task.scheduledEnd);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const diffMs = end - start;
    const diffMins = Math.round(diffMs / 60000);
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Get status badge styling based on job.status (not lead.stage)
  const getStatusBadge = () => {
    const jobStatus = task.status || 'scheduled';
    const colors = {
      'scheduled': 'bg-blue-100 text-blue-800',
      'approved':  'bg-slate-100 text-slate-700',
      'en_route':  'bg-amber-100 text-amber-800',
      'on_site':   'bg-indigo-100 text-indigo-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      // legacy / lead stage fallbacks
      'Approved': 'bg-emerald-100 text-emerald-800',
      'Booked': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
    };
    return colors[jobStatus] || 'bg-gray-100 text-gray-600';
  };

  // Job status label for display
  const getStatusLabel = () => {
    const statusLabels = {
      'scheduled':   'Scheduled',
      'approved':    'Ready',
      'en_route':    'En Route',
      'on_site':     'On Site',
      'in_progress': 'In Progress',
      'completed':   'Completed',
      'cancelled':   'Cancelled',
    };
    return statusLabels[task.status] || (lead?.stage ? `Lead: ${lead.stage}` : 'Scheduled');
  };

  // Format address for display and maps link
  const getAddress = () => {
    if (lead?.addressDisplay) return lead.addressDisplay;
    if (lead?.address) {
      if (typeof lead.address === 'string') return lead.address;
      const { line1, city, state, zip } = lead.address;
      return [line1, city, state, zip].filter(Boolean).join(', ');
    }
    return null;
  };

  const address = getAddress();
  const mapsUrl = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null;

  const handleStartJob = () => {
    updateTask(task.id, { status: 'en_route' });
    onClose();
  };

  const handleMarkComplete = async () => {    // Sprint 4 — single completion contract. Route through completeJob so
    // the backend recurrence engine + invoice / SMS pipeline runs. We
    // close the pop card optimistically since the Firestore listener
    // will re-render the calendar in the next tick.
    try {
      await completeJob(task.id, { quoteId: task.quoteId });
      onClose();
      toast.success('Job marked complete');
    } catch (err) {
      console.error('[JobDetailsPopCard] completeJob failed:', err);
      toast.error('Could not mark complete', {
        description: err?.message || 'Recurring scheduling depends on this — please retry.',
      });
    }
  };

  const handleSendReviewRequest = async () => {
    // Guard: already sent — do nothing
    if (task.reviewRequestedAt) return;

    if (!lead?.phone) {
      toast.error('No phone number on file for this client');
      return;
    }
    if (!companyId) return;

    setSendingReview(true);
    try {
      // Fetch google review link from company settings
      const snap = await getDoc(doc(db, 'companies', companyId));
      const reviewLink = snap.data()?.googleReviewLink;
      if (!reviewLink) {
        toast.error('Add your Google Review link in Settings → Company Profile first');
        setSendingReview(false);
        return;
      }

      // Build personalized message
      const firstName = lead.firstName || lead.name?.trim().split(/\s+/)[0] || null;
      const greeting = firstName ? `Hey ${firstName}` : 'Hey there';
      const message = `${greeting} — really appreciate you having us out today 🙏\n\nIf everything looks good on your end, it would mean a lot if you could share your experience here:\n\n${reviewLink}\n\nThank you again — we're always here if you need anything.`;

      const { getAuth } = await import('firebase/auth');
      const token = await getAuth().currentUser?.getIdToken(true);

      await axios.post(
        `${API}/api/sms/send`,
        { to: lead.phone, message },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Stamp the task so the UI reflects sent state and prevents duplicates
      await updateTask(task.id, { reviewRequestedAt: new Date().toISOString() });

      toast.success('Review request sent');
      // Keep pop card open so user sees the "Sent" confirmation state
    } catch (err) {
      console.error('[ReviewRequest] Failed:', err);
      toast.error('Failed to send review request');
    } finally {
      setSendingReview(false);
    }
  };

  const handleViewProfile = () => {
    if (task.leadId) {
      navigate(`/leads/${task.leadId}`);
    }
    onClose();
  };

  const handleEditSchedule = () => {
    if (onEditSchedule) {
      onEditSchedule(task);
    }
    onClose();
  };

  const handleUnschedule = () => {
    if (onUnschedule) {
      onUnschedule(task);
    }
    onClose();
  };

  const handleOpenWorkOrder = () => {
    if (lead?.id) {
      navigate(`/clients/${lead.id}?tab=jobs`);
    }
    onClose();
  };

  // NOTE: `position` prop is accepted for API compatibility with existing
  // callers (WeekScheduler_EventCard, TechWeekView) but is intentionally
  // ignored. The dialog now renders centered on the viewport so it can no
  // longer be clipped by events near the top / bottom / edge of the
  // calendar. Removing the prop from callers would be a wider refactor
  // outside the scope of this UX fix.

  return (
    <>
      {/* Backdrop + centering container. Click outside → close. */}
      <div
        className="fixed inset-0 z-[999] bg-black/40 flex items-center justify-center p-4"
        onClick={onClose}
        role="presentation"
      >
        {/* Dialog card — centered, capped at 90vh, internal scroll. */}
        <div
          className="w-full max-w-md max-h-[90vh] bg-white rounded-xl shadow-xl border border-gray-200 flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Job details"
          data-testid="job-details-dialog"
        >
        {/* Header */}
        <div className="bg-gradient-to-r from-[var(--cb-orange)] to-[#e85d10] px-4 py-3 shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-base truncate">
                {lead?.name || task.title || 'Scheduled Job'}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge()}`}>
                  {getStatusLabel()}
                </span>
                {task.service && (
                  <span className="text-white/80 text-xs truncate">
                    {task.service}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body — scrolls internally when content exceeds available height. */}
        <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
          {/* Flash note — bright property-level warning, always visible (incl. completed jobs) */}
          {lead?.propertyDetails?.flashNote && (
            <FlashPill propertyDetails={lead.propertyDetails} testId="pop-flash-pill" className="max-w-full" />
          )}

          {/* Next-service override chip — shown on the bound task (or earliest live if floating) */}
          {lead?.recurrence?.nextServiceOverride && (() => {
            const earliest = findEarliestLiveRecurringTask(lead.id, tasks);
            return (
              <OverrideChip
                override={lead.recurrence.nextServiceOverride}
                recurringAmount={lead.recurrence.amount}
                taskId={task.id}
                matchesEarliest={earliest?.id === task.id}
                testId="pop-override-chip"
              />
            );
          })()}

          {/* Schedule Time */}
          <div className="flex items-start gap-3">
            <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-gray-900">
                {formatDate(task.scheduledStart)}
              </div>
              <div className="text-gray-600">
                {formatTime(task.scheduledStart)} - {formatTime(task.scheduledEnd)}
                {getDuration() && (
                  <span className="text-gray-400 ml-1">({getDuration()})</span>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          {address && (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                {mapsUrl ? (
                  <a 
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {address}
                  </a>
                ) : (
                  <span className="text-gray-700">{address}</span>
                )}
              </div>
            </div>
          )}

          {/* Phone */}
          {lead?.phone && (
            <div className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <a 
                href={`tel:${lead.phone}`}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                {lead.phone.length === 10 
                  ? `(${lead.phone.slice(0,3)}) ${lead.phone.slice(3,6)}-${lead.phone.slice(6)}`
                  : lead.phone
                }
              </a>
            </div>
          )}

          {/* Assigned Technician */}
          {task.assignedTo && (
            <div className="flex items-start gap-3">
              <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700">
                {task.assignedToName || task.assignedTo}
              </span>
            </div>
          )}

          {/* Duration */}
          {getDuration() && (
            <div className="flex items-start gap-3">
              <Clock className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700">
                Duration: {getDuration()}
              </span>
            </div>
          )}

          {/* Notes */}
          {(task.notes || lead?.notes) && (
            <div className="flex items-start gap-3">
              <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600 line-clamp-2">
                {task.notes || lead?.notes}
              </p>
            </div>
          )}

          {/* Recurring service — read-only; edit via Lead Detail > Service Plan */}
          {lead?.recurrence?.active && (
            <div className="flex items-center gap-3">
              <RefreshCw className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-xs text-green-700 font-medium" data-testid="pop-recurring-badge">
                {lead.recurrence.planLabel
                  ? lead.recurrence.planLabel
                  : `Recurring — every ${lead.recurrence.interval} days`}
                {lead.recurrence.totalOccurrences != null && (
                  <span className="text-green-600 font-normal ml-1">
                    ({lead.recurrence.completedOccurrences ?? 0} of {lead.recurrence.totalOccurrences})
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Visit Instructions — shown when a service program step is stamped on the job */}
          {task.servicePlanStep && (() => {
            const { displayed: dstep, hasOverride } = resolveDisplayedStep(task);
            const dHasItems = Array.isArray(dstep.items) && dstep.items.length > 0;
            const showItemsBlock = dHasItems || hasOverride;
            return (
            <div
              className="mt-1 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden"
              data-testid="pop-visit-instructions"
            >
              {/* Panel header */}
              <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
                <ClipboardList className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-xs font-semibold text-slate-700">
                  Visit Instructions
                </span>
                {hasOverride && (
                  <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700">
                    <Sparkles className="w-2.5 h-2.5" />
                    Customized
                  </span>
                )}
                {(task.visitNumber || task.seriesIndex) && (
                  <span className="ml-auto text-[10px] font-medium bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">
                    Visit {task.visitNumber || task.seriesIndex}
                    {lead?.recurrence?.totalOccurrences
                      ? ` of ${lead.recurrence.totalOccurrences}`
                      : ''}
                  </span>
                )}
              </div>
              <div className="px-3 py-2 space-y-2">
                {/* Step label */}
                {dstep.label && (
                  <p className="text-xs font-semibold text-slate-800">
                    {dstep.label}
                  </p>
                )}
                {/* Items (catalog) */}
                {showItemsBlock && (
                  <div data-testid="pop-visit-items">
                    <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-0.5 flex items-center gap-1">
                      <Package className="w-2.5 h-2.5" />
                      Items
                    </p>
                    {dHasItems ? (
                      <ul className="space-y-0.5">
                        {dstep.items.map((it, i) => (
                          <li key={`it-${i}`} className="text-xs text-slate-700 flex items-start gap-1">
                            <span className="text-slate-400 mt-0.5">·</span>
                            <span>
                              {it.nameAtSnapshot || '(unknown item)'}
                              {(it.quantity != null || it.unit) && (
                                <span className="text-slate-500">
                                  {' '}{it.quantity != null ? `× ${it.quantity}` : ''}{it.unit ? ` ${it.unit}` : ''}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[11px] text-slate-500 italic">No items for this visit</p>
                    )}
                  </div>
                )}
                {/* Products */}
                {dstep.products?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                      Products / Materials
                    </p>
                    <ul className="space-y-0.5">
                      {dstep.products.map((p, i) => (
                        <li key={i} className="text-xs text-slate-700 flex items-start gap-1">
                          <span className="text-slate-400 mt-0.5">·</span>
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Areas */}
                {dstep.areas?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                      Areas to Treat
                    </p>
                    <ul className="space-y-0.5">
                      {dstep.areas.map((a, i) => (
                        <li key={i} className="text-xs text-slate-700 flex items-start gap-1">
                          <span className="text-slate-400 mt-0.5">·</span>
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Visit-Specific Instructions — override-only addendum */}
                {typeof dstep.visitInstructions === 'string' && dstep.visitInstructions.trim().length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5" data-testid="pop-visit-instructions-section">
                    <p className="text-[10px] font-semibold text-amber-800 uppercase tracking-wide mb-0.5 flex items-center gap-1">
                      <Sparkles className="w-2.5 h-2.5" />
                      Visit-Specific Instructions
                    </p>
                    <p className="text-xs text-amber-900 leading-relaxed whitespace-pre-wrap">
                      {dstep.visitInstructions}
                    </p>
                  </div>
                )}
                {/* Tech notes */}
                {dstep.techNotes && (
                  <div>
                    <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                      Instructions
                    </p>
                    <p className="text-xs text-slate-700 leading-relaxed">
                      {dstep.techNotes}
                    </p>
                  </div>
                )}
                {/* Duration hint */}
                {dstep.estimatedDurationMin && (
                  <p className="text-[10px] text-slate-400">
                    Est. {dstep.estimatedDurationMin} min
                  </p>
                )}
              </div>
            </div>
            );
          })()}
        </div>

        {/* Footer Actions — sticky at the bottom of the dialog. */}
        <div className="border-t border-gray-100 p-3 bg-gray-50 space-y-2 shrink-0">
          {/* Status progression — one-click, no modal */}
          {(task.status === 'scheduled' || task.status === 'approved') && (
            <Button
              size="sm"
              className="w-full text-xs bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleStartJob}
              data-testid="pop-start-job-btn"
            >
              <PlayCircle className="h-3 w-3 mr-1.5" />
              Mark En Route
            </Button>
          )}
          {task.status === 'en_route' && (
            <Button
              size="sm"
              className="w-full text-xs bg-indigo-500 hover:bg-indigo-600 text-white"
              onClick={() => { updateTask(task.id, { status: 'on_site' }); onClose(); }}
              data-testid="pop-on-site-btn"
            >
              <PlayCircle className="h-3 w-3 mr-1.5" />
              Mark On Site
            </Button>
          )}
          {(task.status === 'in_progress' || task.status === 'on_site') && (
            <>
              <PerVisitBillingOverride
                taskId={task.id}
                currentValue={task.chargeOverride}
              />
              <Button
                size="sm"
                className="w-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => setConfirmCompleteOpen(true)}
                data-testid="pop-mark-complete-btn"
              >
                <CheckCircle2 className="h-3 w-3 mr-1.5" />
                Mark Complete
              </Button>
            </>
          )}
          {/* Completion safeguard */}
          <ConfirmCompleteDialog
            open={confirmCompleteOpen}
            onOpenChange={setConfirmCompleteOpen}
            clientName={task.clientName || lead?.name}
            taskId={task.id}
            onConfirm={() => { setConfirmCompleteOpen(false); handleMarkComplete(); }}
          />
          {/* Undo — revert accidental On Site back to En Route.
              Narrow scope: only offered from `on_site`. Never from
              `in_progress` (work started) or `completed` (terminal). */}
          {task.status === 'on_site' && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-amber-700 hover:bg-amber-50"
              onClick={() => {
                updateTask(task.id, { status: 'en_route', arrivedAt: null });
                onClose();
              }}
              data-testid="pop-undo-on-site-btn"
              title="Revert to En Route — use this if arrival was logged by mistake or work cannot begin"
            >
              <PlayCircle className="h-3 w-3 mr-1.5" />
              Undo — back to En Route
            </Button>
          )}
          {task.status === 'completed' && !task.reviewRequestedAt && (
            <Button
              size="sm"
              variant="outline"
              className="w-full text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={handleSendReviewRequest}
              disabled={sendingReview}
              data-testid="pop-send-review-btn"
            >
              <Star className="h-3 w-3 mr-1.5" />
              {sendingReview ? 'Sending…' : 'Send Review Request'}
            </Button>
          )}
          {task.status === 'completed' && task.reviewRequestedAt && (
            <div
              className="flex flex-col items-center gap-0.5 py-1.5 px-2 rounded-md bg-green-50 border border-green-200"
              data-testid="pop-review-sent-indicator"
            >
              <span className="text-xs font-medium text-green-700 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Review Request Sent
              </span>
              <span className="text-[10px] text-green-600">
                {new Date(task.reviewRequestedAt).toLocaleDateString(undefined, {
                  month: 'short', day: 'numeric',
                })}{' '}
                at{' '}
                {new Date(task.reviewRequestedAt).toLocaleTimeString(undefined, {
                  hour: 'numeric', minute: '2-digit',
                })}
              </span>
            </div>
          )}
          {/* Primary action: direct path to work order */}
          {lead?.id && (
            <Button
              size="sm"
              className="w-full text-xs bg-[var(--cb-orange)] hover:bg-[#e85d10] text-white"
              onClick={handleOpenWorkOrder}
              data-testid="open-work-order-btn"
            >
              <ClipboardList className="h-3 w-3 mr-1.5" />
              Open Work Order
            </Button>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={handleViewProfile}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              View Profile
            </Button>
            {/* Assign Technician */}
            <Button
              variant="outline"
              size="sm"
              className={`flex-1 text-xs ${task.assignedTo ? 'border-orange-200 text-orange-700' : ''}`}
              onClick={() => setAssignModalOpen(true)}
              data-testid="pop-assign-tech-btn"
            >
              <UserCheck className="h-3 w-3 mr-1" />
              {task.assignedToName ? task.assignedToName.split(' ')[0] : 'Assign'}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={handleEditSchedule}
            >
              <Calendar className="h-3 w-3 mr-1" />
              Edit Schedule
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleUnschedule}
            >
              <X className="h-3 w-3 mr-1" />
              Unschedule
            </Button>
          </div>
        </div>
        </div>{/* /dialog card */}
      </div>{/* /backdrop + centering container */}

      {/* Technician assignment modal */}
      <JobAssignModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        task={task}
      />
    </>
  );
};

export default JobDetailsPopCard;
